import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { keyBy, mapValues } from "lodash-es";
import {
  useQueryParam,
  useQueryParams,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import { ActiveToolFacetType, IToolFacet, ToolFacetType } from "types";

import { ToolSearchFacetsContainer, ToolSearchResultsContainer } from ".";
import { CommaArrayParam, mapFacetSlugToKey, useActiveFacets } from "../utils";
import { searchTools } from "../state";

interface ToolSearchAppProps {
  facets: IToolFacet[];
  noResultMessage: string;
}

export const ToolSearchContainer: React.FC<ToolSearchAppProps> = ({
  facets,
  noResultMessage,
}) => {
  const dispatch = useDispatch();

  // use facet slug as key (hardcoded NL)
  const initQueryFacets: { [key: string]: any } = mapValues(
    keyBy(facets, "slug"),
    () => withDefault(CommaArrayParam, [])
  );
  const [queryFacets, setQueryFacets] = useQueryParams(initQueryFacets);

  const [queryTerm, setQueryTerm] = useQueryParam(
    ToolFacetType.term,
    StringParam
  );
  const [queryPage, setQueryPage] = useQueryParam("page", NumberParam);

  const { activeFacetsMap: defaultValues, activeFacets } = useActiveFacets({
    facets,
    queryTerm,
    queryFacets,
    setQueryFacets,
    resetTerm: () => setQueryTerm(undefined),
  });

  const clearAllFacets = useCallback(() => {
    // replace all facets in URL
    setQueryFacets(initQueryFacets, "replace");
  }, [initQueryFacets, setQueryFacets]);

  const dispatchSearch = useCallback(() => {
    dispatch(
      searchTools({
        term: queryTerm || "",
        page: queryPage || 1,
        // map slug to key for each facet
        // slug = NL, key = EN
        facets: mapFacetSlugToKey(facets, queryFacets),
      })
    );
  }, [facets, queryFacets, queryTerm, queryPage, dispatch]);

  const areQueryParamsValid: boolean = useMemo(() => {
    const queryFacetsByKey = mapFacetSlugToKey(facets, queryFacets);

    return Object.keys(queryFacetsByKey).every((key) => {
      const queryOptions = queryFacetsByKey[key];
      const facetOptions = facets.find((facet) => facet.key === key)?.options;

      // for free inputs we don't care, always valid
      if (!Array.isArray(queryOptions)) return true;

      // find the options in query that correspond to an option in facets
      const mappedOptions = queryOptions?.filter((queryOptionID) =>
        facetOptions?.find((facetOption) => facetOption.id === queryOptionID)
      );

      // if found options don't match options in query, not valid
      if (mappedOptions.length !== queryOptions.length) {
        return false;
      }

      return true;
    });
  }, [facets, queryFacets]);

  useEffect(() => {
    // if there's an option in query params we don't know
    // reset all facets
    if (!areQueryParamsValid) {
      return clearAllFacets();
    }

    // if query params are valid
    // search tools when query params change
    dispatchSearch();

    // todo: change query param in language switcher
  }, [areQueryParamsValid, clearAllFacets, dispatchSearch]);

  return (
    <div className="tool-search">
      <ToolSearchFacetsContainer
        facets={facets}
        defaultValues={defaultValues}
        activeFacets={activeFacets}
        setQueryFacets={(activeFacet: ActiveToolFacetType) => {
          // reset page if not page 1
          if (queryPage && queryPage > 1) {
            setQueryPage(undefined);
          }

          setQueryFacets(activeFacet);
        }}
        setQueryTerm={(term: string) => {
          // reset page if not page 1
          if (queryPage && queryPage > 1) {
            setQueryPage(undefined);
          }

          // set undefined to remove from URL if empty
          setQueryTerm(term || undefined);
        }}
        clearAllFacets={clearAllFacets}
      />
      <ToolSearchResultsContainer
        setQueryPage={setQueryPage}
        clearAllFacets={clearAllFacets}
        noResultMessage={noResultMessage}
      />
    </div>
  );
};
